import React, { useState, useEffect } from 'react'
import styles from './EventsPage.module.scss'
import { getEvents, getEventCategories, registerForEvent, getMyRegistrations } from '../../../service/EventsService'
import { useAuth } from '../../../contexts/AuthContext'
import Pagination from '../../../components/Pagination/Pagination'
import Loader from '../../../components/Loader/Loader'
import { MoneyIcon, ComputerIcon, CalendarIcon, LocationIcon, DocumentIcon } from '../../../components/CustomIcons'

const EventsPage = ({ embedded = false, goBack }) => {
  const { user, isAuthenticated } = useAuth()
  const [events, setEvents] = useState([])
  const [categories, setCategories] = useState([])
  const [myRegistrations, setMyRegistrations] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [retryAction, setRetryAction] = useState(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [searchTerm, setSearchTerm] = useState('')
  const [view, setView] = useState('events') // events, myregistrations
  const [selectedEvent, setSelectedEvent] = useState(null)
  const [showEventModal, setShowEventModal] = useState(false)
  const [showRegistrationModal, setShowRegistrationModal] = useState(false)
  const [filters, setFilters] = useState({
    search: '',
    category: '',
    date_from: '',
    date_to: '',
    price_range: '',
    status: 'upcoming'
  })
  const [sortBy, setSortBy] = useState('date')

  useEffect(() => {
    loadData()
  }, [currentPage, filters, sortBy, view])

  useEffect(() => {
    if (isAuthenticated) {
      loadMyRegistrations()
    }
  }, [isAuthenticated])

  const loadData = async () => {
    try {
      setLoading(true)
      setError(null)
      await Promise.all([
        loadEvents(),
        loadCategories()
      ])
    } catch (err) {
      setError(`Ошибка загрузки данных: ${err.message}`)
      setRetryAction(() => loadData)
      console.error(err)
    } finally {
      setLoading(false)
    }
  }

  const loadEvents = async () => {
    const response = await getEvents({
      page: currentPage,
      search: filters.search,
      category: filters.category,
      date_from: filters.date_from,
      date_to: filters.date_to,
      price_range: filters.price_range,
      status: filters.status,
      ordering: sortBy
    })
    setEvents(response.results || response.data || [])
    setTotalPages(response.total_pages || 1)
  }

  const loadCategories = async () => {
    try {
      const response = await getEventCategories()
      setCategories(response.data || response || [])
    } catch (err) {
      console.error('Ошибка загрузки категорий:', err)
    }
  }

  const loadMyRegistrations = async () => {
    try {
      const response = await getMyRegistrations()
      setMyRegistrations(response.data || response || [])
    } catch (err) {
      console.error('Ошибка загрузки регистраций:', err)
    }
  }

  const handleEventClick = (event) => {
    setSelectedEvent(event)
    setShowEventModal(true)
  }

  const handleRegisterClick = (event) => {
    if (!isAuthenticated) {
      alert('Для регистрации на событие необходимо авторизоваться')
      return
    }
    setSelectedEvent(event)
    setShowRegistrationModal(true)
  }

  const handleRegistration = async (registrationData) => {
    try {
      await registerForEvent(selectedEvent.id, registrationData)
      setShowRegistrationModal(false)
      await loadMyRegistrations()
      
      // Показываем уведомление об успешной регистрации
      const notification = document.createElement('div')
      notification.className = 'registration-notification'
      notification.textContent = 'Вы успешно зарегистрировались на событие!'
      notification.style.cssText = `
        position: fixed;
        top: 90px;
        right: 20px;
        background: #4CAF50;
        color: white;
        padding: 12px 20px;
        border-radius: 8px;
        z-index: 9999;
        animation: slideInRight 0.3s ease;
        box-shadow: 0 4px 12px rgba(76, 175, 80, 0.3);
      `
      document.body.appendChild(notification)
      setTimeout(() => {
        notification.remove()
      }, 3000)
    } catch (err) {
      console.error('Ошибка регистрации:', err)
      alert(`Ошибка регистрации: ${err.message}`)
    }
  }

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters)
    setCurrentPage(1)
  }

  const handleSearch = (term) => {
    setSearchTerm(term)
    setFilters(prev => ({ ...prev, search: term }))
    setCurrentPage(1)
  }

  const handlePageChange = (page) => {
    setCurrentPage(page)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const handleRetry = () => {
    if (retryAction) {
      retryAction()
    }
  }

  const isRegisteredForEvent = (eventId) => {
    return myRegistrations.some(reg => reg.event.id === eventId)
  }

  if (loading && events.length === 0 && myRegistrations.length === 0) {
    return (
      <div className={styles.loadingContainer}>
        <Loader size="large" />
        <p>Загрузка событий...</p>
      </div>
    )
  }

  return (
    <div className={styles.eventsPage}>
      {!embedded && (
        <div className={styles.header}>
          <h1 className={styles.pageTitle}>
            <span className={styles.icon}>🎉</span>
            События и мероприятия
          </h1>
          <p className={styles.pageSubtitle}>
            Присоединяйтесь к интересным мероприятиям и событиям
          </p>
        </div>
      )}

      {/* Навигация между разделами */}
      <div className={styles.navigation}>
        <div className={styles.tabs}>
          <button 
            className={`${styles.tab} ${view === 'events' ? styles.active : ''}`}
            onClick={() => setView('events')}
          >
            Все события
          </button>
          {isAuthenticated && (
            <button 
              className={`${styles.tab} ${view === 'myregistrations' ? styles.active : ''}`}
              onClick={() => setView('myregistrations')}
            >
              Мои регистрации ({myRegistrations.length})
            </button>
          )}
        </div>
      </div>

      {view === 'events' && (
        <>
          {/* Поиск и фильтры */}
          <div className={styles.controls}>
            <div className={styles.topControls}>
              <input 
                type="text"
                placeholder="Поиск событий..."
                value={searchTerm}
                onChange={(e) => handleSearch(e.target.value)}
                className={styles.searchInput}
              />
              
              <div className={styles.sortSelect}>
                <select 
                  value={sortBy} 
                  onChange={(e) => setSortBy(e.target.value)}
                  className={styles.select}
                >
                  <option value="date">По дате</option>
                  <option value="-date">По дате (убыв.)</option>
                  <option value="price">По цене</option>
                  <option value="-registrations_count">По популярности</option>
                  <option value="name">По названию</option>
                </select>
              </div>
            </div>

            <div className={styles.eventFilters}>
              <div className={styles.filterRow}>
                <div className={styles.filterGroup}>
                  <label>Категория:</label>
                  <select 
                    value={filters.category} 
                    onChange={(e) => handleFilterChange('category', e.target.value)}
                    className={styles.filterSelect}
                  >
                    <option value="">Все категории</option>
                    {categories.map(cat => (
                      <option key={cat.id} value={cat.id}>{cat.name}</option>
                    ))}
                  </select>
                </div>
                
                <div className={styles.filterGroup}>
                  <label>Тип:</label>
                  <select 
                    value={filters.type} 
                    onChange={(e) => handleFilterChange('type', e.target.value)}
                    className={styles.filterSelect}
                  >
                    <option value="">Все типы</option>
                    <option value="online">Онлайн</option>
                    <option value="offline">🏢 Оффлайн</option>
                  </select>
                </div>
                
                <div className={styles.filterGroup}>
                  <label>Стоимость:</label>
                  <select 
                    value={filters.price_range} 
                    onChange={(e) => handleFilterChange('price_range', e.target.value)}
                    className={styles.filterSelect}
                  >
                    <option value="">Любая цена</option>
                    <option value="free">🆓 Бесплатно</option>
                    <option value="paid">Платно</option>
                  </select>
                </div>
              </div>
            </div>
          </div>

          {error && (
            <div className={styles.errorContainer}>
              <div className={styles.errorIcon}>🎉</div>
              <div className={styles.errorContent}>
                <h3 className={styles.errorTitle}>Ошибка загрузки</h3>
                <p className={styles.errorMessage}>{error}</p>
                <button onClick={handleRetry} className={styles.retryButton} disabled={loading}>
                  {loading ? 'Загрузка...' : 'Повторить попытку'}
                </button>
              </div>
            </div>
          )}

          {/* Статистика */}
          <div className={styles.stats}>
            <span>Найдено событий: <strong>{events.length}</strong></span>
            {isAuthenticated && (
              <span>Ваших регистраций: <strong>{myRegistrations.length}</strong></span>
            )}
          </div>

          {/* Сетка событий */}
          <div className={styles.eventsGrid}>
            {events.length === 0 && !loading ? (
              <div className={styles.emptyState}>
                <div className={styles.emptyIcon}>🎉</div>
                <h3>События не найдены</h3>
                <p>Попробуйте изменить фильтры поиска</p>
              </div>
            ) : (
              events.map(event => (
                <div key={event.id} className={styles.eventCard} onClick={() => handleEventClick(event)}>
                  <div className={styles.eventImage}>
                    <img 
                      src={event.image || '/images/default-event.jpg'} 
                      alt={event.title || event.name}
                      onError={(e) => {
                        e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzAwIiBoZWlnaHQ9IjIwMCIgdmlld0JveD0iMCAwIDMwMCAyMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIzMDAiIGhlaWdodD0iMjAwIiBmaWxsPSIjMkEyQTJBIi8+CjxjaXJjbGUgY3g9IjE1MCIgY3k9IjEwMCIgcj0iNDAiIGZpbGw9IiNGRjU3MjIiIG9wYWNpdHk9IjAuNSIvPgo8dGV4dCB4PSIxNTAiIHk9IjEwNSIgZm9udC1mYW1pbHk9IkFyaWFsIiBmb250LXNpemU9IjE4IiBmaWxsPSIjRkY1NzIyIiB0ZXh0LWFuY2hvcj0ibWlkZGxlIj7wn46JPC90ZXh0Pgo8L3N2Zz4K'
                      }}
                    />
                    <div className={styles.eventCategory}>
                      {categories.find(cat => cat.id === event.category)?.name || 'Событие'}
                    </div>
                  </div>
                  <div className={styles.eventInfo}>
                    <h3>{event.title || event.name}</h3>
                    <p className={styles.eventDescription}>{event.description}</p>
                    <div className={styles.eventDate}>
                      <span className={styles.dateIcon}><CalendarIcon size={16} /></span>
                      {new Date(event.start_datetime).toLocaleDateString('ru-RU', {
                        day: 'numeric',
                        month: 'long',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </div>
                    <div className={styles.eventLocation}>
                      <span className={styles.locationIcon}><LocationIcon size={16} /></span>
                      {event.location}
                    </div>
                    <div className={styles.eventPrice}>
                      {event.price > 0 ? `${event.price} Сом` : 'Бесплатно'}
                    </div>
                    <div className={styles.eventActions}>
                      <button 
                        onClick={(e) => {
                          e.stopPropagation()
                          handleEventClick(event)
                        }}
                        className={styles.viewButton}
                      >
                        Подробнее
                      </button>
                      {isAuthenticated && !isRegisteredForEvent(event.id) && (
                        <button 
                          onClick={(e) => {
                            e.stopPropagation()
                            handleRegisterClick(event)
                          }}
                          className={styles.registerButton}
                        >
                          Записаться
                        </button>
                      )}
                      {isRegisteredForEvent(event.id) && (
                        <span className={styles.registeredBadge}>Записан</span>
                      )}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>

          {loading && (
            <div className={styles.loadingMore}>
              <Loader size="medium" />
            </div>
          )}

          {totalPages > 1 && (
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
            />
          )}
        </>
      )}

      {view === 'myregistrations' && isAuthenticated && (
        <div className={styles.myRegistrations}>
          <h3>Мои регистрации</h3>
          {myRegistrations.length === 0 ? (
            <div className={styles.emptyState}>
              <div className={styles.emptyIcon}><DocumentIcon size={48} /></div>
              <h3>Регистраций нет</h3>
              <p>Вы еще не зарегистрировались ни на одно мероприятие</p>
            </div>
          ) : (
            <div className={styles.registrationsList}>
              {myRegistrations.map(registration => (
                <div key={registration.id} className={styles.registrationCard}>
                  <div className={styles.registrationHeader}>
                    <h4>{registration.event?.name}</h4>
                    <span className={`${styles.statusBadge} ${styles[registration.status]}`}>
                      {registration.get_status_display ? registration.get_status_display() : registration.status}
                    </span>
                  </div>
                  <div className={styles.registrationDetails}>
                    <p>
                      <span className={styles.icon}><CalendarIcon size={16} /></span>
                      {new Date(registration.event?.start_datetime).toLocaleDateString('ru-RU', {
                        day: 'numeric',
                        month: 'long',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </p>
                    <p>
                      <span className={styles.icon}><LocationIcon size={16} /></span>
                      {registration.event?.location}
                    </p>
                    <p>
                      <span className={styles.icon}><MoneyIcon size={16} /></span>
                      {registration.event?.price > 0 ? `${registration.event.price} Сом` : 'Бесплатно'}
                    </p>
                    <p>
                      <span className={styles.icon}>🕒</span>
                      Зарегистрирован: {new Date(registration.registered_at).toLocaleDateString('ru-RU')}
                    </p>
                  </div>
                  {registration.ticket && (
                    <div className={styles.ticketInfo}>
                      <p>Билет: #{registration.ticket.ticket_number}</p>
                      <p>Тип: {registration.ticket.get_ticket_type_display ? registration.ticket.get_ticket_type_display() : registration.ticket.ticket_type}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Модальные окна */}
      {showEventModal && selectedEvent && (
        <div className={styles.eventModal}>
          <div className={styles.modalContent}>
            <div className={styles.modalHeader}>
              <h3>{selectedEvent.title || selectedEvent.name}</h3>
              <button 
                className={styles.closeButton}
                onClick={() => setShowEventModal(false)}
              >
                ×
              </button>
            </div>
            <div className={styles.modalBody}>
              <p>{selectedEvent.description}</p>
              <p>Дата: {new Date(selectedEvent.start_datetime).toLocaleDateString('ru-RU')}</p>
              <p>Время: {new Date(selectedEvent.start_datetime).toLocaleTimeString('ru-RU')} - {new Date(selectedEvent.end_datetime).toLocaleTimeString('ru-RU')}</p>
              <p>Место: {selectedEvent.location}</p>
              <p>Адрес: {selectedEvent.address}</p>
              <p>Цена: {selectedEvent.price > 0 ? `${selectedEvent.price} Сом` : 'Бесплатно'}</p>
              <p>Организатор: {selectedEvent.organizer?.username}</p>
              {selectedEvent.capacity && (
                <p>Места: {selectedEvent.available_spots !== null ? `${selectedEvent.available_spots} доступно из ${selectedEvent.capacity}` : `${selectedEvent.capacity} всего`}</p>
              )}
              {isAuthenticated && !isRegisteredForEvent(selectedEvent.id) && (
                <button 
                  onClick={() => handleRegisterClick(selectedEvent)}
                  className={styles.registerButton}
                  disabled={selectedEvent.is_full}
                >
                  {selectedEvent.is_full ? 'Мест нет' : 'Записаться'}
                </button>
              )}
              {isRegisteredForEvent(selectedEvent.id) && (
                <span className={styles.registeredBadge}>Вы записаны</span>
              )}
            </div>
          </div>
        </div>
      )}

      {showRegistrationModal && selectedEvent && (
        <div className={styles.registrationModal}>
          <div className={styles.modalContent}>
            <div className={styles.modalHeader}>
              <h3>Запись на {selectedEvent.title || selectedEvent.name}</h3>
              <button 
                className={styles.closeButton}
                onClick={() => setShowRegistrationModal(false)}
              >
                ×
              </button>
            </div>
            <div className={styles.modalBody}>
              <p>Подтвердите запись на мероприятие.</p>
              <form onSubmit={handleRegistration}>
                <input type="text" placeholder="Полное имя" required />
                <input type="email" placeholder="Email" required />
                <textarea placeholder="Дополнительные заметки (необязательно)"></textarea>
                <button type="submit">Подтвердить запись</button>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default EventsPage